import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

const MUTE_STORAGE_KEY = "ember_audio_muted_v1";

function readMuted() {
  try {
    const v = localStorage.getItem(MUTE_STORAGE_KEY);
    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

function writeMuted(m) {
  try { localStorage.setItem(MUTE_STORAGE_KEY, m ? "1" : "0"); } catch { /* noop */ }
}

/**
 * Adaptive Audio Player with crossfade + persistent mute state (localStorage).
 * Renders a small pill button that toggles the global mute for the app.
 */
export default function AdaptiveAudio({ trackUrl, compact }) {
  const audioA = useRef(null);
  const audioB = useRef(null);
  const activeRef = useRef("A");
  const [muted, setMuted] = useState(readMuted);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!trackUrl) return;
    const current = activeRef.current === "A" ? audioA.current : audioB.current;
    const incoming = activeRef.current === "A" ? audioB.current : audioA.current;
    if (!incoming) return;

    // If same track already playing, keep it
    if (current && current.src && current.src.endsWith(trackUrl.split("/").pop()) && !current.paused) return;

    incoming.src = trackUrl;
    incoming.volume = 0;
    incoming.loop = true;
    incoming.muted = muted;
    const p = incoming.play();
    if (p && p.catch) p.catch(() => {});

    const duration = 900;
    const steps = 18;
    const target = muted ? 0 : 0.55;
    let step = 0;
    const startVol = current ? current.volume : 0;

    const iv = setInterval(() => {
      step += 1;
      const t = step / steps;
      if (current) current.volume = Math.max(0, startVol * (1 - t));
      incoming.volume = Math.min(target, target * t);
      if (step >= steps) {
        clearInterval(iv);
        if (current) { try { current.pause(); } catch { /* ignore */ } }
        activeRef.current = activeRef.current === "A" ? "B" : "A";
      }
    }, duration / steps);
    return () => clearInterval(iv);
  }, [trackUrl, muted]);

  const toggleMute = () => {
    setMuted((m) => {
      const nm = !m;
      writeMuted(nm);
      [audioA.current, audioB.current].forEach((el) => { if (el) el.muted = nm; });
      if (!nm) {
        const current = activeRef.current === "A" ? audioA.current : audioB.current;
        if (current && current.paused) current.play().catch(() => {});
      }
      setStarted(true);
      return nm;
    });
  };

  return (
    <>
      <audio ref={audioA} preload="auto" />
      <audio ref={audioB} preload="auto" />
      <button
        onClick={toggleMute}
        data-testid="audio-mute-toggle"
        aria-label={muted ? "Unmute audio" : "Mute audio"}
        className="panel px-3 py-2 flex items-center gap-2 hover:border-[#e25822] transition-colors"
      >
        {muted ? <VolumeX size={16} /> : <Volume2 size={16} className="gold-text" />}
        {!compact && (
          <span className="text-[10px] tracking-[0.3em] ash-text">
            {muted ? (started ? "MUTED" : "TAP TO HEAR") : "LIVE"}
          </span>
        )}
      </button>
    </>
  );
}
